<p-dialog
  [visible]="visible"
  (visibleChange)="visibleChange.emit($event)"
  [modal]="true"
  [blockScroll]="true"
  [style]="{ width: '90vw', height: '90vh' }"
  [resizable]="false"
  [draggable]="false"
>
  <ng-template pTemplate="header">
    <div class="flex flex-col w-full gap-3 pt-2">
      <span class="text-lg font-semibold">{{ 'pickUpReport.confirm.title' | transloco }}</span>
      <p-steps
        [model]="stepItems"
        [activeIndex]="activeStepIndex"
        [readonly]="true"
        styleClass="w-full"
      />
    </div>
  </ng-template>

  <form [formGroup]="formGroup" class="h-full overflow-y-auto">

    <!-- ===================== STEP 0: Form ===================== -->
    @if (activeStepIndex === 0) {
      <p-accordion [multiple]="false" [value]="'0'">

        <!-- Panel: Abholung -->
        <p-accordion-panel value="0">
          <p-accordion-header>
            <div class="flex items-center gap-2">
              <span>{{ 'pickUpReport.step.form' | transloco }}</span>
              @if (!formGroup.get('pickUpDateTime')?.value) {
                <span class="pi pi-exclamation-triangle text-red-400"></span>
              }
            </div>
          </p-accordion-header>
          <p-accordion-content>
            <div class="grid grid-cols-12 gap-4 mt-2">

              <!-- Abholzeitpunkt -->
              <div class="col-span-12 md:col-span-6">
                <p-iftalabel>
                  <p-datepicker
                    formControlName="pickUpDateTime"
                    [showIcon]="true"
                    [iconDisplay]="'input'"
                    [showTime]="true"
                    [hourFormat]="'24'"
                    dateFormat="dd.mm.yy"
                    [styleClass]="formGroup.get('pickUpDateTime')?.invalid && formGroup.get('pickUpDateTime')?.touched ? 'datepicker-invalid' : ''"
                  />
                  <label>{{ 'pickUpReport.form.pickupDateTime' | transloco }} *</label>
                </p-iftalabel>
              </div>

            </div>
          </p-accordion-content>
        </p-accordion-panel>

        <!-- Panel: BCS -->
        <p-accordion-panel value="1">
          <p-accordion-header>
            <div class="flex items-center gap-2">
              <span>{{ 'pickUpReport.bcs.title' | transloco }}</span>
              @if (!isBcsValid()) {
                <span class="pi pi-exclamation-triangle text-red-400"></span>
              }
            </div>
          </p-accordion-header>
          <p-accordion-content>
            <div class="mt-2">
              <app-body-condition-score
                [careCtrl]="formGroup.get('careCondition')!"
                [nutritionCtrl]="formGroup.get('bcsNutrition')!"
                [temperamentCtrl]="formGroup.get('temperament')!"
              />
            </div>
          </p-accordion-content>
        </p-accordion-panel>

        <!-- Panel: Kontakte -->
        <p-accordion-panel value="2">
          <p-accordion-header>
            <div class="flex items-center gap-2">
              <span>{{ 'pickUpReport.contact.title' | transloco }}</span>
              @if (!hasValidContact()) {
                <span class="pi pi-exclamation-triangle text-red-400"></span>
              }
            </div>
          </p-accordion-header>
          <p-accordion-content>
            <div class="mt-2">

              <div class="flex justify-center mb-3">
                <p-button
                  icon="pi pi-plus"
                  [label]="'pickUpReport.contact.add' | transloco"
                  [text]="true"
                  (click)="addWhereAboutContact()"
                />
              </div>

              @if (whereAboutContacts.length > 0) {
                <p-accordion [multiple]="false" [value]="activeContactPanelIndex" (valueChange)="activeContactPanelIndex = $any($event)">
                  @for (vm of whereAboutContacts; track trackByWhereAboutContact($index, vm); let i = $index) {
                    <p-accordion-panel [value]="i.toString()">
                      <p-accordion-header>
                        <div class="flex items-center justify-between w-full pr-2">
                          <div class="flex items-center gap-2">
                            <strong>{{ getWhereAboutContactTypeLabel(vm.ref.type) }}</strong>
                            @if (!vm.isValid) {
                              <span class="pi pi-exclamation-triangle text-red-400"></span>
                            }
                          </div>
                          <p-button
                            icon="pi pi-trash"
                            severity="danger"
                            [text]="true"
                            (click)="$event.stopPropagation(); removeWhereAboutContact(i)"
                          />
                        </div>
                      </p-accordion-header>
                      <p-accordion-content>
                        <div class="grid grid-cols-12 gap-4 mb-3">
                          <div class="col-span-12 md:col-span-6">
                            <p-iftalabel>
                              <p-select
                                [options]="whereAboutContactTypeOptions"
                                [ngModel]="vm.ref.type"
                                (ngModelChange)="onWhereAboutContactTypeChanged(i, $event)"
                                [ngModelOptions]="{ standalone: true }"
                                optionLabel="label"
                                optionValue="value"
                                appendTo="body"
                                class="w-full"
                              />
                              <label>{{ 'pickUpReport.contact.type' | transloco }}</label>
                            </p-iftalabel>
                          </div>
                        </div>
                        <app-choose-contact
                          [newContact]="$any(vm.contact)"
                          [isExistingContact]="vm.isExisting"
                          (newContactUpdated)="onWhereAboutNewContactUpdated(i, $event)"
                          (validationStatusChanged)="onWhereAboutValidationChanged(i, $event)"
                          (existingContactSelected)="onWhereAboutExistingSelected(i, $event)"
                        />
                      </p-accordion-content>
                    </p-accordion-panel>
                  }
                </p-accordion>
              }

            </div>
          </p-accordion-content>
        </p-accordion-panel>

      </p-accordion>
    }

    <!-- ===================== STEP 1: PDF Vorschau ===================== -->
    @if (activeStepIndex === 1) {
      <div class="flex justify-center h-full">
        <div class="w-full xl:w-2/3">
          <app-pdf-viewer
            [pdf]="reportPreviewBlob"
            height="calc(90vh - 250px)"
          />
        </div>
      </div>
    }

    <!-- ===================== STEP 2: Unterschriften ===================== -->
    @if (activeStepIndex === 2) {
      <div class="grid grid-cols-12 gap-4 p-2">
        <div class="col-span-12 md:col-span-6">
          <h4 class="mb-2">{{ 'pickUpReport.signature.pickup' | transloco }}</h4>
          <app-signature-pad
            (signatureSaved)="formGroup.get('pickupSignature')?.setValue($event)"
            (signatureCleared)="formGroup.get('pickupSignature')?.setValue(null)"
          />
        </div>

        <div class="col-span-12 md:col-span-6">
          <h4 class="mb-2">{{ 'pickUpReport.signature.employee' | transloco }}</h4>
          <app-signature-pad
            (signatureSaved)="formGroup.get('employeeSignature')?.setValue($event)"
            (signatureCleared)="formGroup.get('employeeSignature')?.setValue(null)"
          />
        </div>

        <div class="col-span-12 md:col-span-6">
          <p-iftalabel>
            <input
              pInputText
              formControlName="signatureLocation"
              class="w-full"
              [ngClass]="{ '!border-red-500': formGroup.get('signatureLocation')?.invalid }"
            />
            <label>{{ 'pickUpReport.signature.location' | transloco }} *</label>
          </p-iftalabel>
        </div>

        <div class="col-span-12 md:col-span-6">
          <p-iftalabel>
            <p-datepicker
              formControlName="signatureDate"
              [showIcon]="true"
              [iconDisplay]="'input'"
              dateFormat="dd.mm.yy"
              [styleClass]="formGroup.get('signatureDate')?.invalid ? 'datepicker-invalid' : ''"
            />
            <label>{{ 'pickUpReport.signature.date' | transloco }} *</label>
          </p-iftalabel>
        </div>
      </div>
    }

  </form>

  <!-- Footer -->
  <ng-template pTemplate="footer">
    <div class="flex items-center justify-end gap-2">

      @if (activeStepIndex === 0) {
        <p-button
          [label]="'general.close' | transloco"
          [text]="true"
          (click)="close()"
        />
        <p-button
          [label]="'animalAdd.nextStep' | transloco"
          icon="pi pi-angle-right"
          iconPos="right"
          [disabled]="!isStep1Valid() || isLoadingPreview"
          [loading]="isLoadingPreview"
          (click)="nextStep()"
        />
      }

      @if (activeStepIndex === 1) {
        <p-button
          [label]="'animalAdd.lastStep' | transloco"
          icon="pi pi-angle-left"
          [text]="true"
          (click)="prevStep()"
        />
        <p-button
          [label]="'animalAdd.nextStep' | transloco"
          icon="pi pi-angle-right"
          iconPos="right"
          [disabled]="isLoadingPreview"
          [loading]="isLoadingPreview"
          (click)="nextStep()"
        />
      }

      @if (activeStepIndex === 2) {
        <p-button
          [label]="'animalAdd.lastStep' | transloco"
          icon="pi pi-angle-left"
          [text]="true"
          (click)="prevStep()"
        />
        <p-button
          [label]="'modal.save' | transloco"
          icon="pi pi-save"
          [text]="true"
          [disabled]="!isPdfBlockValid() || isSaving"
          [loading]="isSaving"
          (click)="finish(false)"
        />
        <p-button
          [label]="'pickUpReport.saveAndSend' | transloco"
          icon="pi pi-send"
          iconPos="right"
          [disabled]="!isPdfBlockValid() || isSaving"
          [loading]="isSaving"
          (click)="finish(true)"
        />
      }

    </div>
  </ng-template>

</p-dialog>

<!-- Send Dialog -->
<app-send-found-report-dialog
  [id]="savedWhereaboutId"
  [animal]="animal"
  [(visible)]="sendDialogVisible"
  [sendFn]="sendFnBound"
  (sent)="onSendDialogClosed()"
/>
